// src/components/NotificationBell.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Bell, Calendar, Radio, XCircle, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { subscribeToNotifications, markNotificationRead, markAllNotificationsRead } from '../live/services/eventService';
import { useCurrentUser } from '../contexts/UserContext';
import type { AppNotification } from '../live/types/events';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (!currentUser) return;

    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    // Small delay to avoid race conditions with Firestore listeners during rapid mount/unmount
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        try {
          unsubscribe = subscribeToNotifications(currentUser.uid, (notifs) => {
            if (isMounted) {
              setNotifications(notifs);
            }
          });
        } catch (err) {
          // Silently handle subscription errors during rapid mount/unmount
          console.warn('Failed to subscribe to notifications:', err);
        }
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      // Wrap unsubscribe in try-catch to handle Firestore internal state errors
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (err) {
          // Silently handle cleanup errors - these occur during rapid unmount cycles
          console.warn('Notification cleanup error (safe to ignore):', err);
        }
      }
    };
  }, [currentUser?.uid]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!currentUser) return;

    if (!notification.read) {
      await markNotificationRead(currentUser.uid, notification.id);
    }

    setIsOpen(false);

    if (notification.type === 'draw_winner') {
      navigate('/analytics');
    } else if (notification.eventId) {
      navigate(`/live/${notification.eventId}`);
    }
  };

  const handleMarkAllRead = async () => {
    if (!currentUser) return;

    await markAllNotificationsRead(currentUser.uid);
  };

  const getNotificationIcon = (type: AppNotification['type']) => {
    switch (type) {
      case 'event_reminder':
        return <Calendar className="w-4 h-4 text-purple-400" />;
      case 'event_starting':
        return <Radio className="w-4 h-4 text-yellow-400" />;
      case 'event_live':
        return <Radio className="w-4 h-4 text-red-400" />;
      case 'event_cancelled':
        return <XCircle className="w-4 h-4 text-zinc-400" />;
      case 'draw_winner':
        return <Trophy className="w-4 h-4 text-yellow-400" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const formatTime = (timestamp: any) => {
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-colors hover:bg-zinc-800"
      >
        <Bell className="w-5 h-5 text-zinc-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-12 w-80 bg-zinc-900 rounded-xl shadow-lg border border-zinc-700 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
            <h3 className="font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-yellow-400 hover:text-yellow-300"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-500">
                No notifications yet
              </div>
            ) : (
              notifications.slice(0, 20).map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-zinc-800 transition-colors ${
                    !notification.read ? 'bg-zinc-800/50' : ''
                  }`}
                >
                  <div className="mt-1">{getNotificationIcon(notification.type)}</div>
                  <div className="flex-1 min-w-0">
                    {notification.title && (
                      <p className={`text-xs font-semibold ${!notification.read ? 'text-yellow-400' : 'text-zinc-400'}`}>
                        {notification.title}
                      </p>
                    )}
                    <p className={`text-sm ${!notification.read ? 'text-white font-medium' : 'text-zinc-300'}`}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {formatTime(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
